import React, { useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { PricePoint } from './HistoricalUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

interface Props {
  routePrices: PricePoint[];
  cssClass?: string;
  hideLegend?: boolean;
  currency?: string;
  showTicks?: boolean;
}

export const CurveMiniChart: React.FC<Props> = ({
  routePrices,
  cssClass = 'w-full h-32',
  hideLegend = false,
  currency = '$',
  showTicks = false,
}) => {
  const chartRef = useRef<any>(null);

  // Filter visible prices with valid data
  const visiblePrices = routePrices?.filter(({ price }) => !!price) || [];

  if (visiblePrices.length === 0) {
    return <div className="w-full h-20 flex items-center justify-center text-gray-400 text-xs">No curve data</div>;
  }

  const prices = visiblePrices.map((point) => +(point.price || 0));
  const baseColor = visiblePrices[0]?.color || '#10b981';
  const first = prices[0];
  const last = prices[prices.length - 1];
  const isUp = last >= first;
  const lineColor = isUp ? '#10b981' : '#ef4444';

  const getData = () => {
    const labels = visiblePrices.map((point) => point.name);

    return {
      labels,
      datasets: [
        {
          label: 'Curve',
          data: prices,
          borderColor: lineColor,
          backgroundColor: isUp ? 'rgba(16,185,129,0.06)' : 'rgba(239,68,68,0.06)',
          pointBackgroundColor: '#fff',
          pointBorderColor: lineColor,
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: lineColor,
          borderWidth: 2,
          fill: true,
          tension: 0.35,
        },
      ],
    };
  };

  const getOptions = (): any => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1f2937',
        titleColor: '#f3f4f6',
        bodyColor: '#fff',
        titleFont: { size: 11 },
        bodyFont: { size: 12, weight: 'bold' },
        padding: { top: 6, bottom: 6, left: 10, right: 10 },
        cornerRadius: 6,
        displayColors: false,
        callbacks: {
          title: (items: any[]) => visiblePrices[items[0]?.dataIndex]?.name || '',
          label: (context: any) => `${currency}${context.parsed.y.toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
        },
      },
    },
    scales: {
      x: {
        display: showTicks,
        grid: { display: false },
        ticks: {
          maxRotation: 0,
          autoSkip: true,
          font: { size: 10 },
          color: '#9ca3af',
        },
      },
      y: {
        display: !hideLegend,
        grid: {
          display: showTicks,
          color: 'rgba(0,0,0,0.04)',
        },
        ticks: {
          font: { size: 10 },
          color: '#9ca3af',
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  });

  return (
    <div className={cssClass}>
      <Line ref={chartRef} options={getOptions()} data={getData()} />
    </div>
  );
};
